import React, { useState, useEffect } from 'react';
import { Tabs, Button, Dropdown, message } from 'antd';
import {
  FileExcelOutlined,
  DownloadOutlined,
  AuditOutlined,
  TeamOutlined,
  UserOutlined,
  LoginOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Layout from '../../components/Layout';
import AllActivityTab from './AllActivityTab';
import AdminActivityTab from './AdminActivityTab';
import CustomerActivityTab from './CustomerActivityTab';
import LoginActivityTab from './LoginActivityTab';
import { getActivityLogStats, exportActivityLogs } from '../../services/activityLogService';
import PageHeadingBanner from '../../components/shared/PageHeadingBanner';
import DashboardStatCard from '../../components/shared/DashboardStatCard';
import './ActivityLogs.css';

const ActivityLogs = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    adminActions: 0,
    customerActions: 0,
    loginAttempts: 0,
    failedLogins: 0,
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await getActivityLogStats();
        if (res?.status) {
          setStats(res.data);
        }
      } catch (err) {
        console.error('Failed to fetch activity stats:', err);
      }
    };
    fetchStats();
  }, []);

  // ── Export Handlers ────────────────────────────────────────────────────
  const getExportData = async () => {
    const res = await exportActivityLogs({});
    if (!res?.status || !res.data?.length) {
      message.warning('No records to export');
      return null;
    }
    return res.data.map((log) => ({
      'User Name': log.userName,
      'User Type': log.userType,
      'Action': log.action,
      'Module': log.module,
      'IP Address': log.ipAddress || '',
      'Device': log.deviceType || '',
      'Channel': log.channel || '',
      'Login Status': log.loginStatus || '',
      'Account Number': log.accountNumber || '',
      'Role': log.role || '',
      'Timestamp': log.timestamp,
    }));
  };

  const handleExportCSV = async () => {
    const data = await getExportData();
    if (!data) return;

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Activity Logs');
    const csvOutput = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `activity_logs_${new Date().toISOString().split('T')[0]}.csv`);
    message.success('CSV exported successfully');
  };

  const handleExportExcel = async () => {
    const data = await getExportData();
    if (!data) return;

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Activity Logs');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `activity_logs_${new Date().toISOString().split('T')[0]}.xlsx`);
    message.success('Excel file exported successfully');
  };

  const exportMenuItems = [
    {
      key: 'csv',
      label: 'Export as CSV',
      icon: <DownloadOutlined />,
      onClick: handleExportCSV,
    },
    {
      key: 'excel',
      label: 'Export as Excel',
      icon: <FileExcelOutlined />,
      onClick: handleExportExcel,
    },
  ];

  // ── Tabs Configuration ────────────────────────────────────────────────
  const tabItems = [
    {
      key: 'all',
      label: (
        <span>
          <AuditOutlined style={{ marginRight: 6 }} />
          All Activity
        </span>
      ),
      children: <AllActivityTab />,
    },
    {
      key: 'admin',
      label: (
        <span>
          <SafetyCertificateOutlined style={{ marginRight: 6 }} />
          Admin Activity
        </span>
      ),
      children: <AdminActivityTab />,
    },
    {
      key: 'customer',
      label: (
        <span>
          <UserOutlined style={{ marginRight: 6 }} />
          Customer Activity
        </span>
      ),
      children: <CustomerActivityTab />,
    },
    {
      key: 'login',
      label: (
        <span>
          <LoginOutlined style={{ marginRight: 6 }} />
          Login Activity
        </span>
      ),
      children: <LoginActivityTab />,
    },
  ];

  return (
    <Layout>
      <div className="activity-logs-wrapper" style={{ padding: '0 24px' }}>
        {/* ── Hero Header ──────────────────────────────────────────── */}
        <PageHeadingBanner
          icon={<AuditOutlined />}
          title="Activity Logs"
          subtitle="Monitor and audit all user activities across the platform"
          actions={
            <Dropdown menu={{ items: exportMenuItems }} trigger={['click']}>
              <Button
                icon={<DownloadOutlined />}
                className="phb-btn-solid"
                size="large"
              >
                Export Logs
              </Button>
            </Dropdown>
          }
        />

        {/* ── Stats Cards ───────────────────────────────────────────── */}
        <div className="activity-stats-row">
          <div className="ds-stats-grid cols-4">
            <DashboardStatCard
              icon={<AuditOutlined />}
              value={stats.total}
              label="Total Activities"
              iconBg="#fff3eb"
              iconColor="#ff6700"
            />
            <DashboardStatCard
              icon={<SafetyCertificateOutlined />}
              value={stats.adminActions}
              label="Admin Actions"
              iconBg="#f9f0ff"
              iconColor="#722ed1"
            />
            <DashboardStatCard
              icon={<TeamOutlined />}
              value={stats.customerActions}
              label="Customer Actions"
              iconBg="#e6f7ff"
              iconColor="#1890ff"
            />
            <DashboardStatCard
              icon={<LoginOutlined />}
              value={stats.loginAttempts}
              label="Login Attempts"
              iconBg="#f6ffed"
              iconColor="#52c41a"
              extra={
                stats.failedLogins > 0 ? (
                  <span style={{ color: '#ff4d4f', marginLeft: 6, fontSize: 10 }}>
                    ({stats.failedLogins} failed)
                  </span>
                ) : null
              }
            />
          </div>
        </div>

        {/* ── Tabs ──────────────────────────────────────────────────── */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className="activity-logs-tabs"
        />
      </div>
    </Layout>
  );
};

export default ActivityLogs;
